/**
 * Single source of truth for engine / networking tunables.
 *
 * Gameplay balance (stats, wave sizes, countdown lengths) lives in
 * `data/entities.json`. This file only holds the few knobs that affect
 * simulation timing and network smoothness. Keep this list short.
 */
export const NET_CONFIG = {
  /** Fixed simulation rate in Hz. The server ticks exactly this often. */
  TICK_HZ: 30,
  /** Colyseus state patch rate in Hz (how often deltas are flushed to clients). */
  PATCH_HZ: 20,
  /**
   * Client render delay in milliseconds for remote entities. Remote players and
   * enemies are rendered this far in the past so snapshot interpolation always
   * has two snapshots to interpolate between. ~2 patch intervals.
   */
  INTERP_DELAY_MS: 100,
  /** Client -> server input send rate in Hz. */
  INPUT_SEND_HZ: 30,
  /**
   * Max positional error (px) tolerated during client reconciliation before the
   * predicted local player is snapped instead of eased.
   */
  RECONCILE_SNAP_DIST: 96,
  /** Reconciliation smoothing factor per frame when error is below the snap distance. */
  RECONCILE_EASE: 0.2,
} as const;

export const TICK_DT = 1 / NET_CONFIG.TICK_HZ;
export const TICK_MS = 1000 / NET_CONFIG.TICK_HZ;

/**
 * feature 5.3a — floor-hazard tick cadence (ms). A `damage` hazard hits each
 * hero standing in it once per this interval (per-hazard accumulator, like a
 * periodic status); a `slow` hazard re-applies its slow for `2 ×` this every
 * tick so it lapses shortly after the hero leaves the zone.
 */
export const HAZARD_TICK_MS = 500;

export const ROOM_CONFIG = {
  MAX_PLAYERS: 6,
  CODE_LENGTH: 6,
  /** Characters used for room codes (no ambiguous 0/O/1/I). */
  CODE_ALPHABET: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
} as const;

export type GamePhase =
  'lobby' | 'select' | 'countdown' | 'match' | 'wavebreak' | 'gameover' | 'victory';
